/**
 * Strip common quote suffixes (including doubled USDT) for TradingView search.
 */
export function normalizeBaseSymbolForTradingView(raw: string): string {
  let s = raw.trim().toUpperCase().replace(/[-_/]/g, "");
  const suffixes = ["USDT", "USDC", "BUSD", "USD", "PERP", "PERPETUAL"];
  let changed = true;
  while (changed && s.length > 0) {
    changed = false;
    for (const suf of suffixes) {
      if (s.endsWith(suf) && s.length > suf.length) {
        s = s.slice(0, -suf.length);
        changed = true;
      }
    }
  }
  return s.replace(/[^A-Z0-9]/g, "") || raw.trim().toUpperCase();
}

export interface TradingViewSymbol {
  symbol: string;
  description: string;
  type: string;
  exchange: string;
  typespecs?: string[];
  prefix?: string;
  source_id?: string;
}

interface TradingViewSymbolResponse {
  symbols: TradingViewSymbol[];
}

export interface TradingViewMatch {
  exchange: string;
  symbol: string;
}

const stripHtml = (value: string) => value.split(/<[^>]*>?/g).join("");

const plainSymbol = (value: string) => stripHtml(value).toUpperCase();

const PREFERRED_EXCHANGES = [
  "Binance",
  "Bybit",
  "Gate",
  "KuCoin",
  "Hyperliquid",
  "Aster",
];

const getExchangePriority = (exchange: string): number => {
  const index = PREFERRED_EXCHANGES.findIndex(
    (preferred) => exchange.toLowerCase() === preferred.toLowerCase()
  );
  return index === -1 ? PREFERRED_EXCHANGES.length : index;
};

/**
 * TradingView's local search only returns the first page. For majors such as
 * BTC and ETH that page is filled with USD pairs, so a bare-ticker query never
 * contains a USDT market and the chart is treated as missing.
 */
export function pickTradingViewMatch(
  symbols: TradingViewSymbol[],
  searchKey: string
): TradingViewMatch | null {
  const exactPrefix = `${searchKey}USDT`;
  const matches = symbols.filter((item) => {
    const symbol = plainSymbol(item.symbol);
    return (
      item.typespecs?.includes("crypto") &&
      !symbol.includes("PREMIUM") &&
      symbol.includes("USDT") &&
      symbol.includes(searchKey)
    );
  });

  if (matches.length === 0) {
    return null;
  }

  const sortedMatches = [...matches].sort((a, b) => {
    const exactA = plainSymbol(a.symbol).startsWith(exactPrefix) ? 0 : 1;
    const exactB = plainSymbol(b.symbol).startsWith(exactPrefix) ? 0 : 1;
    if (exactA !== exactB) return exactA - exactB;
    return getExchangePriority(a.exchange) - getExchangePriority(b.exchange);
  });

  const match = sortedMatches[0];

  return {
    exchange: match.prefix ?? match.source_id ?? match.exchange,
    symbol: plainSymbol(match.symbol),
  };
}

async function queryTradingViewSymbols(text: string): Promise<TradingViewSymbol[]> {
  const fetchFn = typeof globalThis === "undefined" ? undefined : globalThis.fetch;
  if (!fetchFn) {
    return [];
  }

  const endpoint = `https://symbol-search.tradingview.com/local_search/v3/?text=${encodeURIComponent(
    text
  )}&hl=1`;
  const response = await fetchFn(endpoint);

  if (!response.ok) {
    throw new Error("Failed to fetch symbol metadata");
  }

  const data = (await response.json()) as TradingViewSymbolResponse;
  return data.symbols ?? [];
}

export async function fetchTradingViewSymbol(
  rawSymbol: string
): Promise<TradingViewMatch | null> {
  const fetchFn = typeof globalThis === "undefined" ? undefined : globalThis.fetch;
  if (!fetchFn) {
    return null;
  }

  const searchKey = normalizeBaseSymbolForTradingView(rawSymbol);
  if (!searchKey) {
    return null;
  }

  const pairMatch = pickTradingViewMatch(
    await queryTradingViewSymbols(`${searchKey}USDT`),
    searchKey
  );
  if (pairMatch) {
    return pairMatch;
  }

  return pickTradingViewMatch(await queryTradingViewSymbols(searchKey), searchKey);
}
